import { Link } from "react-router-dom";
import { LogOut, Shield, User, Info, Mail, Briefcase, FileText, Lock, XCircle, Home, LayoutDashboard, HelpCircle, Newspaper, MessageSquarePlus } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import FeedbackModal from "@/components/FeedbackModal";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ThemeToggle } from "@/components/ThemeToggle";
import { DebugToggle } from "@/components/DebugToggle";
import { useState } from "react";
import RevokeConsentModal from "@/components/RevokeConsentModal";
import { useConsent } from "@/contexts/ConsentContext";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface MobileSideMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MobileSideMenu = ({ open, onOpenChange }: MobileSideMenuProps) => {
  const { user, isAdmin, signOut } = useAuth();
  const { hasAccepted, revokeConsent } = useConsent();
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const handleLinkClick = () => {
    onOpenChange(false);
  };

  const handleRevokeConsent = async () => {
    await revokeConsent();
    setIsRevokeModalOpen(false);
    onOpenChange(false);
    toast.success("Consentement révoqué", {
      description: "Vous devrez accepter à nouveau les CGU pour effectuer des recherches"
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[280px] p-0">
        <SheetHeader className="p-6 pb-4">
          <SheetTitle className="text-left">Menu</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col h-full">
          {/* User Info */}
          {user && (
            <div className="px-6 py-4 bg-secondary/30">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {user.email}
                  </p>
                  {isAdmin && (
                    <p className="text-xs text-muted-foreground">
                      Administrateur
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <Separator />

          {/* Navigation Links */}
          <nav className="flex-1 p-4 space-y-4">
            {/* Catégorie Navigation */}
            <div>
              <div className="px-3 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Navigation</span>
              </div>
              <div className="space-y-1">
                <Link
                  to="/"
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors"
                >
                  <Home className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">Accueil</span>
                </Link>

                <Link
                  to="/about"
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors"
                >
                  <Info className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">À propos</span>
                </Link>
                
                <Link
                  to="/contact"
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors"
                >
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">Contact</span>
                </Link>

                <button
                  onClick={() => setIsFeedbackModalOpen(true)}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors w-full text-left"
                >
                  <MessageSquarePlus className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">Feedback</span>
                </button>

                <Link
                  to="/faq"
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors"
                >
                  <HelpCircle className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">FAQ</span>
                </Link>

                <Link
                  to="/blog"
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors"
                >
                  <Newspaper className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">Blog</span>
                </Link>
              </div>
            </div>

            <Separator />

            {/* Catégorie Légal */}
            <div>
              <div className="px-3 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Légal</span>
              </div>
              <div className="space-y-1">
                <Link
                  to="/cgu"
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors"
                >
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">CGU</span>
                </Link>

                <Link
                  to="/privacy-policy"
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors"
                >
                  <Lock className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">Confidentialité</span>
                </Link>

                {hasAccepted && (
                  <button
                    onClick={() => setIsRevokeModalOpen(true)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-destructive/10 text-destructive transition-colors w-full text-left"
                  >
                    <XCircle className="h-5 w-5" />
                    <span className="text-sm font-medium">Révoquer consentement</span>
                  </button>
                )}
              </div>
            </div>

            <Separator />

            {/* Catégorie Compte */}
            <div>
              <div className="px-3 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Compte</span>
              </div>
              <div className="space-y-1">
                {!user && (
                  <Link
                    to="/auth"
                    onClick={handleLinkClick}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors"
                  >
                    <User className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm font-medium">Connexion</span>
                  </Link>
                )}

                {user && !isAdmin && (
                  <Link
                    to="/dashboard"
                    onClick={handleLinkClick}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors"
                  >
                    <LayoutDashboard className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm font-medium">Mon Espace</span>
                  </Link>
                )}

                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={handleLinkClick}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors"
                  >
                    <Shield className="h-5 w-5 text-primary" />
                    <span className="text-sm font-medium text-primary">
                      Dashboard Admin
                    </span>
                  </Link>
                )}

                {user && (
                  <button
                    onClick={async () => {
                      await signOut();
                      handleLinkClick();
                      window.location.href = "/";
                    }}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors w-full text-left"
                  >
                    <LogOut className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm font-medium">Déconnexion</span>
                  </button>
                )}
              </div>
            </div>
          </nav>

          <Separator />

          {/* Footer */}
          <div className="px-4 py-3 space-y-3">
            <div className="flex items-center justify-center gap-2">
              <DebugToggle />
              <ThemeToggle />
            </div>
            <div className="text-center text-xs text-muted-foreground font-mono">
              © 2025 PartXplorer
            </div>
          </div>
        </div>

        <RevokeConsentModal
          open={isRevokeModalOpen}
          onOpenChange={setIsRevokeModalOpen}
          onConfirm={handleRevokeConsent}
        />
        <FeedbackModal
          open={isFeedbackModalOpen}
          onOpenChange={setIsFeedbackModalOpen}
        />
      </SheetContent>
    </Sheet>
  );
};

export default MobileSideMenu;
